import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { KeyRound, AlertCircle } from 'lucide-react';

/**
 * Blocking modal shown after login when the user has `must_change_password=true`.
 * This happens after an admin sets their initial password or resets it — we force
 * the user to replace it with one only they know before they can use the app.
 */
export function ForcePasswordChangeModal() {
  const { user, token, API, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const mustChange = !!user?.must_change_password;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (newPassword === currentPassword) {
      toast.error('New password must differ from your current password');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirmation do not match');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password changed. Please sign in again with your new password.');
      // Log out so they sign back in with the new password → clean session state,
      // and ensures the must_change_password flag is definitively cleared.
      setTimeout(() => logout(), 400);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={mustChange} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="force-change-password-modal"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-amber-600" />
            Change Your Password
          </DialogTitle>
          <DialogDescription>
            Your administrator set a temporary password for you. For security, please choose your own password before continuing.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="fcp-current">Current (temporary) password *</Label>
            <Input
              id="fcp-current"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              data-testid="fcp-current-password"
            />
          </div>
          <div>
            <Label htmlFor="fcp-new">New password *</Label>
            <Input
              id="fcp-new"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              data-testid="fcp-new-password"
            />
            <p className="text-xs text-slate-500 mt-1">Minimum 8 characters</p>
          </div>
          <div>
            <Label htmlFor="fcp-confirm">Confirm new password *</Label>
            <Input
              id="fcp-confirm"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              data-testid="fcp-confirm-password"
            />
          </div>

          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>You'll be signed out after changing your password. Sign back in with your new password to continue.</span>
          </div>

          <DialogFooter className="sm:justify-between">
            <Button type="button" variant="ghost" onClick={logout} data-testid="fcp-logout">
              Sign out
            </Button>
            <Button type="submit" disabled={submitting} data-testid="fcp-submit">
              {submitting ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
